import { useEffect, useRef } from "react";

/**
 * =============================================================================
 * EL BOTON ATRAS CIERRA LA HOJA
 * =============================================================================
 * En un movil, el gesto para "volver" es el boton atras del sistema, no una X
 * en una esquina. Con un asistente de cuatro pasos abierto, ese gesto sacaba al
 * terapeuta de la pagina entera y se llevaba por delante todo lo que llevara
 * hecho.
 *
 * Esto mete una entrada de historial mientras la hoja esta abierta, para tener
 * algo que consumir: al pulsar atras se cierra la hoja y la aplicacion se queda
 * donde estaba.
 *
 * Notas de por que esta escrito asi:
 *
 *  - `history.state` se conserva tal cual y solo se le anade una marca propia.
 *    React Router guarda ahi su indice de navegacion; machacarlo lo dejaria sin
 *    saber en que punto del historial esta.
 *  - Se usa `pushState` con la MISMA url. Con HashRouter eso no navega a ningun
 *    sitio, que es justo lo que se quiere: solo apilar un paso atras.
 *  - Al cerrar la hoja con la X o con un boton se retira esa entrada, para no
 *    dejar pasos muertos que obliguen a pulsar atras dos veces.
 *
 * @param {boolean}  abierta
 * @param {Function} alCerrar
 */
export function useHistorialHoja(abierta, alCerrar) {
  // Si la entrada la puso este hook. Sin esto, cerrar la hoja retiraria un paso
  // de navegacion de verdad.
  const entradaPropia = useRef(false);
  const cerrarRef = useRef(alCerrar);
  cerrarRef.current = alCerrar;

  useEffect(() => {
    if (!abierta || typeof window === "undefined") return undefined;

    window.history.pushState(
      { ...window.history.state, reviHoja: true },
      "",
      window.location.href,
    );
    entradaPropia.current = true;

    const alVolver = () => {
      entradaPropia.current = false;
      cerrarRef.current?.();
    };

    window.addEventListener("popstate", alVolver);

    return () => {
      window.removeEventListener("popstate", alVolver);
      // La hoja se cerro por otra via (la X, guardar, cancelar): se retira la
      // entrada para que atras no tenga que pulsarse dos veces.
      if (entradaPropia.current && window.history.state?.reviHoja) {
        entradaPropia.current = false;
        window.history.back();
      }
    };
  }, [abierta]);
}
